import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { endSilentTurn } from "@/features/tutor/utils/silentTurnClock";

// Bumped on every dispatch so a slow failure cannot settle the turn that
// replaced it.
let dispatchSeq = 0;

export const dispatchTutorTurn = (): boolean => {
  const store = useChatSettingsStore.getState();
  store.applyPendingTutorAudio();
  store.markTurnDispatched();
  dispatchSeq += 1;
  return useChatSettingsStore.getState().effectiveTutorAudioOn;
};

export const settleTutorTurn = (): void => {
  endSilentTurn();
  useChatSettingsStore.getState().markTurnSettled();
};

/**
 * Dispatch a turn and hand its audio mode to the request that starts it.
 *
 * The turn stays marked in flight until something real ends it — START into
 * playback, an interrupt, a reset, a disconnect — or until the request itself
 * fails, which is what this adds. A turn cannot be assumed dead because it is
 * slow: the LLM retry ladder can run for a long time before the first text
 * segment, and a frontend that gives up while the backend is still holding
 * MockTTSService will apply an unmute the backend never hears, then wait
 * forever for audio that turn was never going to produce.
 */
export const startTutorTurn = async <T>(
  send: (tutorAudioOn: boolean) => Promise<T>,
): Promise<T> => {
  const tutorAudioOn = dispatchTutorTurn();
  const seq = dispatchSeq;
  try {
    return await send(tutorAudioOn);
  } catch (error) {
    if (seq === dispatchSeq) settleTutorTurn();
    throw error;
  }
};
